import { useContext, useState, useEffect } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import { SurveyViewContext } from '../Context'

import { useAuthStore } from 'Store/Auth'
import { useSurveyStore } from 'Store/Survey'
import { shallow } from 'zustand/shallow'
import { toast } from 'react-toastify'

interface ViewModelOptions {
  variant?: 'modal' | 'inline'
  category?: 'general' | 'sales'
}

const ViewModel = ({ variant = 'modal', category = 'general' }: ViewModelOptions = {}) => {
  const { id } = useParams()
  const location = useLocation()
  const { isOpenImageUploaderModal, setIsOpenImageUploaderModal } = useContext(SurveyViewContext)
  const {
    selected,
    getOne,
    getImagePath,
    uploadImage,
    updateImageDescription,
    deleteImage,
    getSalesImagePath,
    uploadSalesImage,
    updateSalesImageDescription,
    deleteSalesImage,
  } = useSurveyStore(
    state => ({
      selected: state.selected,
      getOne: state.getOne,
      getImagePath: state.getImagePath,
      uploadImage: state.uploadImage,
      updateImageDescription: state.updateImageDescription,
      deleteImage: state.deleteImage,
      getSalesImagePath: state.getSalesImagePath,
      uploadSalesImage: state.uploadSalesImage,
      updateSalesImageDescription: state.updateSalesImageDescription,
      deleteSalesImage: state.deleteSalesImage,
    }),
    shallow
  )

  const isSales = category === 'sales'
  const resolveImagePath = isSales ? getSalesImagePath : getImagePath
  const resolveUploadImage = isSales ? uploadSalesImage : uploadImage
  const resolveUpdateImageDescription = isSales
    ? updateSalesImageDescription
    : updateImageDescription
  const resolveDeleteImage = isSales ? deleteSalesImage : deleteImage
  const [attachImage, setAttachImage] = useState<any[]>([])
  const [openLightBox, setOpenLightBox] = useState<boolean>(false)
  const [lightBoxImage, setLightBoxImage] = useState<string>('' as any)
  const [lightBoxAlt, setLightBoxAlt] = useState<string>('' as any)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const { profile } = useAuthStore(state => ({ profile: state.profile }), shallow)
  const userRoleId = profile?.roleId

  const isView = location.search.includes('view')

  const initFetch = () => {
    if (id) {
      const images = isSales
        ? selected?.survey?.surveySalesUploadImages ?? []
        : selected?.survey?.surveyUploadImages ?? []
      setAttachImage(
        images.map((img: any) => ({
          ...img,
          imgPath:
            process.env.REACT_APP_BACKEND_API_URL + resolveImagePath(parseInt(id), img.fileName),
          isEditNote: false,
        }))
      )
    }
  }

  const handleClose = () => {
    setIsOpenImageUploaderModal(false)
  }

  const onChangeNote = (idx: number, note: string) => {
    const newAttachImage = [...attachImage]
    newAttachImage[idx].description = note
    newAttachImage[idx].isEditNote = true
    setAttachImage(newAttachImage)
  }

  const saveNote = async (idx: number) => {
    if (id) {
      const attachImg = attachImage[idx]
      const { success } = await resolveUpdateImageDescription(
        parseInt(id),
        attachImg.id,
        attachImg.description
      )

      if (!success) {
        toast.error('Error saving note')
        return
      } else {
        toast.success('Note saved')
        await getOne(parseInt(id))
      }
    }
  }

  const openImage = (idx: number) => {
    if (id) {
      const imgPath = attachImage[idx].imgPath
      const imgFileName = attachImage[idx].fileName
      setLightBoxImage(imgPath)
      setLightBoxAlt(imgFileName)
      setOpenLightBox(true)
    }
  }

  const closeImage = () => {
    setOpenLightBox(false)
  }

  const addImage = async (imgs: FileList) => {
    if (id) {
      setIsLoading(true)
      try {
        const { success } = await resolveUploadImage(parseInt(id), imgs)

        if (!success) {
          toast.error('Error adding file')
          return
        } else {
          toast.success('Add file successfully')
          await getOne(parseInt(id))
        }
      } finally {
        setIsLoading(false)
      }
    }
  }

  const removeImage = async (idx: number) => {
    const attachImg = attachImage[idx]
    if (id) {
      const { success } = await resolveDeleteImage(parseInt(id), attachImg.id)
      if (!success) {
        toast.error('Error removing file')
        return
      } else {
        toast.success('Remove file successfully')
        await getOne(parseInt(id))
      }
    }
  }

  const downloadFile = (idx: number) => {
    const item = attachImage[idx]
    const link = document.createElement('a')
    link.href = item.imgPath
    link.download = item.fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    toast.info('กำลังดาวน์โหลดไฟล์...')
  }

  useEffect(() => {
    if (variant === 'inline' && id) {
      const surveyId = parseInt(id, 10)
      if (!Number.isNaN(surveyId) && selected?.survey?.id !== surveyId) {
        getOne(surveyId)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant, id, selected?.survey?.id])

  useEffect(() => {
    initFetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, selected])

  return {
    userRoleId,
    isView,
    open: isOpenImageUploaderModal,
    handleClose,
    addImage,
    attachImage,
    removeImage,
    openLightBox,
    openImage,
    closeImage,
    lightBoxImage,
    lightBoxAlt,
    onChangeNote,
    saveNote,
    isLoading,
    downloadFile,
  }
}

export default ViewModel
